// components/PeriodDropdown/SimplePeriodDropdown.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Button, Typography, Popper, Paper, MenuList, MenuItem, ClickAwayListener } from '@mui/material';
import { useSelector } from 'react-redux';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { DateRangeRsuite } from '../DateRange/Date';
import type { SimplePeriodDropdownProps, SubPeriodOption, DateRange, Props, ParentWithSub } from './types';
import { PERIOD_OPTIONS, MONTHS, QUARTERS, HALVES, CAL_MONTH_LABELS } from './constants';
import { getDateRange, formatDateRange, toUnix, dayRangeForTs, normalizeRangeToSeconds } from './utils';

// ---- Reusable debounce hook (typed, no stale closures) ----
function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, delay: number) {
  const fnRef = useRef(fn);
  useEffect(() => {
    fnRef.current = fn;
  }, [fn]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const debounced = useCallback(
    (...args: A) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        fnRef.current(...args);
      }, delay);
    },
    [delay]
  );

  useEffect(() => cancel, [cancel]);

  return [debounced, cancel] as const;
}

export const SimplePeriodDropdown: React.FC<Props> = ({
  value,
  onChange,
  title = 'Period:',
  showDateText = true,
  includeToday = true,
  useFYFromRedux = true,
  monthIdsAreCalendar = true,
}) => {
  const [period, setPeriod] = useState<'today' | 'year' | 'month' | 'quarter' | 'half' | 'custom'>('custom');
  const [subPeriod, setSubPeriod] = useState<SubPeriodOption[]>([]);
  const [mainAnchorEl, setMainAnchorEl] = useState<null | HTMLElement>(null);
  const [subAnchorEl, setSubAnchorEl] = useState<null | HTMLElement>(null);
  const [mainIndex, setMainIndex] = useState<number>(-1);
  const [subIndex, setSubIndex] = useState<number>(-1);
  const [openSubParent, setOpenSubParent] = useState<ParentWithSub | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Local editing state for custom range, as a tuple [fromMs, toMs]
  type RsuiteTuple = [number | null, number | null];
  const [customMs, setCustomMs] = useState<RsuiteTuple | null>(null);

  const mainRefs = useRef<(HTMLLIElement | null)[]>([]);
  const subRefs = useRef<(HTMLLIElement | null)[]>([]);

  // --- FY from Redux ---
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const financialYear: any = useSelector((state: any) => state?.userPrefrenceandReducer?.financialYear);
  const rawFyStart: number | null = typeof financialYear?.startdate === 'number' ? financialYear.startdate : null;
  const rawFyEnd: number | null = typeof financialYear?.enddate === 'number' ? financialYear.enddate : null;
  const fyStart = typeof rawFyStart === 'number' ? (rawFyStart > 1e12 ? Math.floor(rawFyStart / 1000) : rawFyStart) : null;
  const fyEnd = typeof rawFyEnd === 'number' ? (rawFyEnd > 1e12 ? Math.floor(rawFyEnd / 1000) : rawFyEnd) : null;
  const fyReady = typeof fyStart === 'number' && typeof fyEnd === 'number';

  const nowSec = Math.floor(Date.now() / 1000);
  const todayInsideFY = fyReady ? nowSec >= (fyStart as number) && nowSec <= (fyEnd as number) : true;
  const menuOptions = includeToday && todayInsideFY ? PERIOD_OPTIONS : PERIOD_OPTIONS.filter((p) => p.id !== 'today');

  // Month submenu options (FY order if requested)
  const getMonthOptions = (): SubPeriodOption[] => {
    if (monthIdsAreCalendar || !fyReady) return MONTHS; // Jan..Dec ids 1..12
    const fyStartMonth = new Date((fyStart as number) * 1000).getMonth(); // 0..11
    const arr: SubPeriodOption[] = [];
    for (let i = 0; i < 12; i++) {
      const mIndex = (fyStartMonth + i) % 12;
      arr.push({ id: i + 1, label: CAL_MONTH_LABELS[mIndex].label });
    }
    return arr;
  };

  const getSubPeriodOptions = (id: string): SubPeriodOption[] => {
    switch (id) {
      case 'month':
        return getMonthOptions();
      case 'quarter':
        return QUARTERS;
      case 'half':
        return HALVES;
      default:
        return [];
    }
  };

  // ---------- FY helpers ----------
  const clampToFY = (from: number, to: number) => ({
    from: Math.max(from, fyStart as number),
    to: Math.min(to, fyEnd as number),
  });

  const rangeForFYWindow = (startMonthOffset: number, monthsLen: number) => {
    const base = new Date((fyStart as number) * 1000);
    const baseYear = base.getFullYear();
    const baseMonth = base.getMonth(); // 0..11
    const targetMonthAbs = baseMonth + startMonthOffset;
    const targetYear = baseYear + Math.floor(targetMonthAbs / 12);
    const targetMonth = ((targetMonthAbs % 12) + 12) % 12;
    const start = new Date(targetYear, targetMonth, 1, 0, 0, 0, 0);
    const end = new Date(targetYear, targetMonth + monthsLen, 0, 23, 59, 59, 999);
    return clampToFY(toUnix(start), toUnix(end));
  };

  const calendarIdToFyOffset = (calendarId: number) => {
    const fyStartMonth = new Date((fyStart as number) * 1000).getMonth(); // 0..11
    const calMonth = calendarId - 1; // 0..11
    return (calMonth - fyStartMonth + 12) % 12; // 0..11
  };

  const rangeForFYMonthId = (id: number, useCalendarIds: boolean) => {
    const offset = useCalendarIds ? calendarIdToFyOffset(id) : id - 1;
    return rangeForFYWindow(offset, 1);
  };

  const rangeForFYQuarter = (qid: string) => {
    const map: Record<string, number> = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 };
    return rangeForFYWindow(map[qid] ?? 0, 3);
  };

  const rangeForFYHalf = (hid: string) => {
    const map: Record<string, number> = { H1: 0, H2: 6 };
    return rangeForFYWindow(map[hid] ?? 0, 6);
  };

  // ---------- Canonical range calculators (both FY + generic) ----------
  const computeRangeFY = (
    periodId: 'today' | 'year' | 'month' | 'quarter' | 'half' | 'custom',
    subId?: string | number,
    useCalendarIdsForMonth = monthIdsAreCalendar
  ): DateRange => {
    if (!fyReady) return { from: null, to: null };
    switch (periodId) {
      case 'year':
        return { from: fyStart as number, to: fyEnd as number };
      case 'today': {
        const clamped = Math.min(Math.max(nowSec, fyStart as number), fyEnd as number);
        const d = dayRangeForTs(clamped);
        return clampToFY(d.from, d.to);
      }
      case 'month': {
        const m = Number(subId);
        if (!m || m < 1 || m > 12) return { from: fyStart as number, to: fyEnd as number };
        return rangeForFYMonthId(m, useCalendarIdsForMonth);
      }
      case 'quarter':
        return rangeForFYQuarter(String(subId));
      case 'half':
        return rangeForFYHalf(String(subId));
      default:
        return { from: null, to: null };
    }
  };

  const computeRangeGeneric = (periodId: 'today' | 'year' | 'month' | 'quarter' | 'half' | 'custom', subId?: string | number): DateRange =>
    normalizeRangeToSeconds(getDateRange(periodId, subId));

  // ---------- equality + inference ----------
  const sameRange = (a?: DateRange, b?: DateRange) =>
    !!a && !!b && a.from != null && a.to != null && b.from != null && b.to != null && a.from === b.from && a.to === b.to;

  const inferFromValue = (raw?: DateRange) => {
    const v = normalizeRangeToSeconds(raw);
    if (v.from == null || v.to == null) return { period: 'custom' as const };

    const cands: Array<{ period: 'today' | 'year' | 'month' | 'quarter' | 'half'; subId?: string | number; range: DateRange }> = [];

    if (useFYFromRedux && fyReady) {
      cands.push({ period: 'year', range: computeRangeFY('year') });
      if (todayInsideFY) cands.push({ period: 'today', range: computeRangeFY('today') });
      for (let m = 1; m <= 12; m++) {
        cands.push({ period: 'month', subId: m, range: computeRangeFY('month', m, true) });
        cands.push({ period: 'month', subId: m, range: computeRangeFY('month', m, false) });
      }
      (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => cands.push({ period: 'quarter', subId: q, range: computeRangeFY('quarter', q) }));
      (['H1', 'H2'] as const).forEach((h) => cands.push({ period: 'half', subId: h, range: computeRangeFY('half', h) }));
    } else {
      cands.push({ period: 'year', range: computeRangeGeneric('year') });
      if (includeToday) cands.push({ period: 'today', range: computeRangeGeneric('today') });
      for (let m = 1; m <= 12; m++) cands.push({ period: 'month', subId: m, range: computeRangeGeneric('month', m) });
      (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) =>
        cands.push({ period: 'quarter', subId: q, range: computeRangeGeneric('quarter', q) })
      );
      (['H1', 'H2'] as const).forEach((h) => cands.push({ period: 'half', subId: h, range: computeRangeGeneric('half', h) }));
    }

    const hit = cands.find((c) => sameRange(v, c.range));
    if (hit) return { period: hit.period, subId: hit.subId };
    return { period: 'custom' as const };
  };

  /** Infer subId for currently open parent from `value` (no persistence). */
  const inferSubIdForParent = (parent: ParentWithSub, v: DateRange): string | number | undefined => {
    const vv = normalizeRangeToSeconds(v);
    if (vv.from == null || vv.to == null) return undefined;

    const tryList: Array<{ id: string | number; range: DateRange }> = [];

    if (useFYFromRedux && fyReady) {
      if (parent === 'month') {
        for (let m = 1; m <= 12; m++) tryList.push({ id: m, range: computeRangeFY('month', m, monthIdsAreCalendar) });
      } else if (parent === 'quarter') {
        (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => tryList.push({ id: q, range: computeRangeFY('quarter', q) }));
      } else if (parent === 'half') {
        (['H1', 'H2'] as const).forEach((h) => tryList.push({ id: h, range: computeRangeFY('half', h) }));
      }
    } else {
      if (parent === 'month') {
        for (let m = 1; m <= 12; m++) tryList.push({ id: m, range: computeRangeGeneric('month', m) });
      } else if (parent === 'quarter') {
        (['Q1', 'Q2', 'Q3', 'Q4'] as const).forEach((q) => tryList.push({ id: q, range: computeRangeGeneric('quarter', q) }));
      } else if (parent === 'half') {
        (['H1', 'H2'] as const).forEach((h) => tryList.push({ id: h, range: computeRangeGeneric('half', h) }));
      }
    }

    const hit = tryList.find((c) => sameRange(vv, c.range));
    return hit?.id;
  };

  // ---------- validate `value` vs FY; then infer selection ----------
  useEffect(() => {
    const v = normalizeRangeToSeconds(value);
    if (useFYFromRedux && fyReady && v.from != null && v.to != null) {
      const outOfFY = v.from < (fyStart as number) || v.to > (fyEnd as number);
      if (outOfFY) {
        setError('Selected range is outside the current financial year.');
        return;
      }
    }
    setError(null);
    const inf = inferFromValue(value);
    setPeriod(inf.period);
    // sync local ms when not actively editing custom
    if (!(period === 'custom' && subAnchorEl)) {
      const fromMs = v.from != null ? v.from  : null;
      const toMs = v.to != null ? v.to  : null;
      setCustomMs([fromMs, toMs]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.from, value?.to, fyStart, fyEnd, useFYFromRedux, monthIdsAreCalendar, includeToday]);

  // ---------- debounced emit for custom typing ----------
  const emit = (range: DateRange) => {
    onChange?.(normalizeRangeToSeconds(range));
  };
  const [emitDebouncedCustom, cancelEmitDebouncedCustom] = useDebouncedCallback((range: DateRange) => {
    emit(range);
  }, 400);

  // ---------- UI handlers ----------
  const openSubmenu = (parentId: ParentWithSub, anchor: HTMLElement) => {
    const subs = getSubPeriodOptions(parentId);
    setSubPeriod(subs);
    setSubAnchorEl(anchor);
    setOpenSubParent(parentId);

    const nval = normalizeRangeToSeconds(value);
    const inferred = inferSubIdForParent(parentId, nval);
    const idx =
      inferred != null
        ? Math.max(
            0,
            subs.findIndex((s) => s.id === inferred)
          )
        : 0;
    setSubIndex(idx);
    setTimeout(() => subRefs.current[idx]?.focus(), 0);
  };

  const closeSubmenu = () => {
    setSubAnchorEl(null);
    setOpenSubParent(null);
    setSubIndex(-1);
  };

  const handleMainClick = (e: React.MouseEvent<HTMLElement>) => {
    const opening = !mainAnchorEl;
    setMainAnchorEl(opening ? e.currentTarget : null);
    closeSubmenu();
    if (opening) {
      const idx = Math.max(
        0,
        menuOptions.findIndex((p) => p.id === period)
      );
      setMainIndex(idx);
      setTimeout(() => mainRefs.current[idx]?.focus(), 0);
    } else {
      setMainIndex(-1);
    }
  };

  // Custom date change (object in ms -> tuple + debounced emit in s)
  const handleCustomDateChangeMs = (rangeMs: { from: number | null; to: number | null }) => {
    rangeMs = { from: rangeMs.from ? rangeMs.from  : null, to: rangeMs.to ? rangeMs.to  : null };
    setCustomMs([rangeMs.from ?? null, rangeMs.to ?? null]);

    let from = rangeMs.from != null ? Math.floor(rangeMs.from) : null;
    let to = rangeMs.to != null ? Math.floor(rangeMs.to) : null;

    if (from != null && to != null && useFYFromRedux && fyReady) {
      from = Math.max(from, fyStart as number);
      to = Math.min(to, fyEnd as number);
    }

    setPeriod('custom');
    emitDebouncedCustom({ from, to });
  };

  const handleMainSelect = (
    option: { id: 'today' | 'year' | 'month' | 'quarter' | 'half' | 'custom'; label: string },
    anchor: HTMLElement
  ) => {
    if (option.id === 'today' || option.id === 'year') {
      setPeriod(option.id);
      const r = useFYFromRedux && fyReady ? computeRangeFY(option.id) : computeRangeGeneric(option.id);
      emit(r);
      setMainAnchorEl(null);
      setMainIndex(-1);
      closeSubmenu();
      setCustomMs(null);
      return;
    }

    if (option.id === 'custom') {
      const v = normalizeRangeToSeconds(value);
      const fromMs = v.from != null ? v.from  : null;
      const toMs = v.to != null ? v.to  : null;

      setPeriod('custom');
      setSubPeriod([]);
      setSubAnchorEl(anchor);
      setOpenSubParent(null);
      setSubIndex(-1);
      setCustomMs([fromMs, toMs]);
      return;
    }

    openSubmenu(option.id as ParentWithSub, anchor);
  };

  const handleSubSelect = (subId: string | number) => {
    const parent = openSubParent ?? (['month', 'quarter', 'half'].includes(period) ? (period as ParentWithSub) : null);

    const r =
      useFYFromRedux && fyReady
        ? parent === 'month'
          ? rangeForFYMonthId(Number(subId), monthIdsAreCalendar)
          : parent === 'quarter'
          ? rangeForFYQuarter(String(subId))
          : rangeForFYHalf(String(subId))
        : computeRangeGeneric(parent ?? period, subId);

    if (parent) setPeriod(parent);

    emit(r);
    setMainAnchorEl(null);
    setMainIndex(-1);
    closeSubmenu();
    setCustomMs(null);
  };

  const handleMainKeyDown = (e: React.KeyboardEvent) => {
    if (!mainAnchorEl) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMainIndex((p) => (p + 1) % menuOptions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMainIndex((p) => (p - 1 + menuOptions.length) % menuOptions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const option = menuOptions[mainIndex];
      if (option) handleMainSelect(option as any, mainRefs.current[mainIndex]!);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const option = menuOptions[mainIndex];
      if (!option) return;
      if (option.id === 'custom') {
        handleMainSelect(option as any, mainRefs.current[mainIndex]!);
        return;
      }
      const subs = getSubPeriodOptions(option.id);
      if (subs.length > 0) openSubmenu(option.id as ParentWithSub, mainRefs.current[mainIndex]!);
    } else if (e.key === 'Escape') {
      setMainAnchorEl(null);
      setMainIndex(-1);
      closeSubmenu();
      setCustomMs(null);
    }
  };

  const handleSubKeyDown = (e: React.KeyboardEvent) => {
    if (!subAnchorEl) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSubIndex((p) => (p + 1) % subPeriod.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSubIndex((p) => (p - 1 + subPeriod.length) % subPeriod.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = subPeriod[subIndex];
      if (opt) handleSubSelect(opt.id);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      closeSubmenu();
      setTimeout(() => mainRefs.current[mainIndex]?.focus(), 0);
    } else if (e.key === 'Escape') {
      closeSubmenu();
      setCustomMs(null);
    }
  };

  useEffect(() => {
    if (mainIndex >= 0) mainRefs.current[mainIndex]?.focus();
  }, [mainIndex]);
  useEffect(() => {
    if (subIndex >= 0) subRefs.current[subIndex]?.focus();
  }, [subIndex]);

  const nval = normalizeRangeToSeconds(value);
  const dateText = formatDateRange(nval.from, nval.to, period);
  const inferredSubId = openSubParent ? inferSubIdForParent(openSubParent, nval) : undefined;

  // derived (prop) ms if not editing
  const propFromMs = nval.from != null ? nval.from  : null;
  const propToMs = nval.to != null ? nval.to  : null;

  const isOpen = Boolean(mainAnchorEl);
  const handlePrimaryMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setMainAnchorEl(null);
      setMainIndex(-1);
      closeSubmenu();
      setCustomMs(null);
    }
  };
  return (
    <ClickAwayListener
      onClickAway={(event) => {
        const target = event.target as Node;
        if (mainAnchorEl?.contains(target)) return;
        if (subAnchorEl?.contains(target)) return;

        setMainAnchorEl(null);
        setMainIndex(-1);
        closeSubmenu();
        cancelEmitDebouncedCustom();
        setCustomMs(null);
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1">{title}</Typography>

        <Button
          variant="outlined"
          onClick={handleMainClick}
          onKeyDown={handleMainKeyDown}
          endIcon={isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          aria-haspopup="listbox"
          aria-expanded={isOpen ? 'true' : 'false'}
          sx={{ minWidth: 185, justifyContent: 'space-between', gap: 0.75, pr: 1.5 }}
          disabled={!!error}
        >
          {menuOptions.find((p) => p.id === period)?.label ?? 'Custom'}
        </Button>

        {showDateText && (
          <Typography variant="body2" sx={{ ml: 1, fontSize: '14px' }}>
            {dateText}
          </Typography>
        )}

        {error && (
          <Typography variant="caption" color="error" sx={{ ml: 1 }}>
            {error}
          </Typography>
        )}

        {/* Main menu */}
        <Popper open={Boolean(mainAnchorEl)} anchorEl={mainAnchorEl} sx={{ zIndex: 9999 }} placement="bottom-start" disablePortal>
          <Paper sx={{ width: mainAnchorEl?.offsetWidth }}>
            <MenuList onKeyDown={handlePrimaryMenuKeyDown}>
              {menuOptions.map((option, idx) => (
                <MenuItem
                  key={option.id}
                  ref={(el) => (mainRefs.current[idx] = el)}
                  sx={{
                    '&.Mui-focusVisible, &.Mui-focused, &:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    },
                  }}
                  selected={option.id === period}
                  onMouseEnter={() => {
                    setMainIndex(idx);
                    mainRefs.current[idx]?.focus();
                  }}
                  onClick={(e) => handleMainSelect(option as any, e.currentTarget)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </Popper>

        {/* Submenu (list) */}
        {subPeriod.length > 0 && openSubParent && (
          <Popper open={Boolean(subAnchorEl)} anchorEl={subAnchorEl} sx={{ zIndex: 9999 }} placement="right-start" disablePortal>
            <Paper>
              <MenuList onKeyDown={handleSubKeyDown}>
                {subPeriod.map((sub, idx) => {
                  const isSelected = sub.id === inferredSubId;
                  return (
                    <MenuItem
                      key={sub.id}
                      autoFocus={idx === subIndex}
                      ref={(el) => (subRefs.current[idx] = el)}
                      sx={{
                        '&.Mui-focusVisible, &.Mui-focused, &:focus-visible': {
                          outline: '2px solid',
                          outlineColor: 'primary.main',
                          outlineOffset: '2px',
                        },
                      }}
                      selected={isSelected}
                      onMouseEnter={() => {
                        setSubIndex(idx);
                        subRefs.current[idx]?.focus();
                      }}
                      onClick={() => handleSubSelect(sub.id)}
                    >
                      {sub.label}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </Paper>
          </Popper>
        )}

        {/* Submenu (custom date) */}
        {!openSubParent && period === 'custom' && (
          <Popper open={Boolean(subAnchorEl)} anchorEl={subAnchorEl} sx={{ zIndex: 9999 }} placement="right-start" disablePortal>
            <Paper sx={{ p: 1 }}>
              <DateRangeRsuite
                value={customMs ?? [propFromMs, propToMs]} // tuple in ms
                onChange={handleCustomDateChangeMs} // object {from,to} in ms
                shouldDisableDate={(date) => {
                  if (!fyStart || !fyEnd) return false;
                  const dateTime = date.getTime() / 1000;
                  return dateTime < fyStart || dateTime > fyEnd;
                }}
                onValidationSuccess={() => {
                  setMainAnchorEl(null);
                  setMainIndex(-1);
                  closeSubmenu();
                }}
                onOk={() => {
                  setMainAnchorEl(null);
                  setMainIndex(-1);
                  closeSubmenu();
                }}
              />
            </Paper>
          </Popper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
